import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, UserResponse } from '@handover/shared-types';
import { userApi } from './userApi';

// Initial state
const initialState: UserResponse = {
  userDocProfileData: null,
  usersProfileData: null,
  message: '',
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Manually set user profile data
    setUserState: (state, action: PayloadAction<{ userDocProfileData: User }>) => {
      state.userDocProfileData = action.payload.userDocProfileData;
    },
    // Manually set all users' profile data
    setAllUserState: (state, action: PayloadAction<{ usersProfileData: User[] }>) => {
      state.usersProfileData = action.payload.usersProfileData;
    },
    setMessageState: (state, action: PayloadAction<{ message: string }>) => {
      state.message = action.payload.message;
    },
  },
  extraReducers: builder => {
    // Handle getMe query (fetch the current user's profile)
    builder.addMatcher(userApi.endpoints.getMe.matchFulfilled, (state, { payload }) => {
      state.userDocProfileData = payload.userDocProfileData;
      state.message = payload.message;
    });
    builder.addMatcher(userApi.endpoints.getMe.matchPending, state => {
      state.message = 'Loading user profile...';
    });
    builder.addMatcher(userApi.endpoints.getMe.matchRejected, (state, { error }) => {
      state.userDocProfileData = null;
      state.message = `Failed to load user profile: ${error.message}`;
    });

    // Handle getAllUser query (fetch all users' profiles)
    builder.addMatcher(userApi.endpoints.getAllUser.matchFulfilled, (state, { payload }) => {
      state.usersProfileData = payload.usersProfileData;
      state.message = payload.message;
    });
    builder.addMatcher(userApi.endpoints.getAllUser.matchPending, state => {
      state.message = 'Loading all users...';
    });
    builder.addMatcher(userApi.endpoints.getAllUser.matchRejected, (state, { error }) => {
      state.usersProfileData = null;
      state.message = `Failed to load users: ${error.message}`;
    });

    // Handle updateUser mutation (update the user's profile)
    builder.addMatcher(userApi.endpoints.updateUser.matchFulfilled, (state, { payload }) => {
      state.userDocProfileData = payload.userDocProfileData;
      state.message = payload.message;
    });
    builder.addMatcher(userApi.endpoints.updateUser.matchPending, state => {
      state.message = 'Updating user profile...';
    });
    builder.addMatcher(userApi.endpoints.updateUser.matchRejected, (state, { error }) => {
      state.message = `Failed to update user profile: ${error.message}`;
    });
  },
});

// Export the actions
export const { setUserState, setAllUserState, setMessageState } = userSlice.actions;

// Export the reducer
export default userSlice.reducer;
